import { useContext, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { SampleModalContext, scrollToId } from "@/App";

const lineVariants = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] } },
};

const STATS = [
  { value: "35+", label: "Years of experience" },
  { value: "Wide Range", label: "of handwoven fabrics" },
  { value: "18+", label: "Export markets" },
  { value: "100%", label: "Woven to order" },
];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      variants={lineVariants}
      transition={{ ...lineVariants.show.transition, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const openSamples = useContext(SampleModalContext);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" data-testid="hero-section" ref={ref} className="grain-overlay relative min-h-screen overflow-hidden bg-navy">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <video
          data-testid="hero-background-video"
          className="h-full w-full object-cover opacity-55"
          autoPlay
          muted
          loop
          playsInline
          poster="/images/hero-mill-poster.jpg"
        >
          <source src="/videos/hero-mill.webm" type="video/webm" />
          <source src="/videos/hero-mill.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/40 to-navy" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-5 pb-14 pt-32 sm:px-8">
        <motion.div initial="hidden" animate="show">
          <MaskedLine delay={0.15}>
            <span className="text-xs font-semibold uppercase tracking-[0.4em] text-champagne">
              Est. 1991 · Bhagalpur — The Silk City of India
            </span>
          </MaskedLine>

          <h1 className="mt-6 font-serif text-4xl font-semibold leading-[1.08] text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.3}>Handloom mastery,</MaskedLine>
            <MaskedLine delay={0.42}>
              <em className="text-gold">milled to order</em>
            </MaskedLine>
            <MaskedLine delay={0.54}>for the world's finest houses.</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300"
          >
            Founded by Mr. Anand Kumar Nathani, P.A. International runs its handloom mill in
            Bhagalpur, staffed by experienced weavers with a deep knowledge of the craft. We supply
            pure silk, raw silk, matka, dupion, linen, cotton, viscose, raffia and recycled yarn
            fabrics to fashion and home décor houses worldwide.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-request-samples-btn"
              onClick={openSamples}
              className="group flex items-center gap-3 bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-navy transition-colors duration-300 hover:bg-champagne"
            >
              Request Samples
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
            <button
              data-testid="hero-explore-fabrics-btn"
              onClick={() => scrollToId("#fabrics")}
              className="border border-white/30 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Explore the Fabric Range
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          data-testid="hero-stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 1 }}
          className="mt-16 grid grid-cols-2 gap-6 border-t border-white/15 pt-8 md:grid-cols-4"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-serif text-3xl font-semibold text-white">{s.value}</div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">{s.label}</div>
            </div>
          ))}
        </motion.div>

        <motion.button
          data-testid="hero-scroll-cue"
          onClick={() => scrollToId("#about")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="absolute bottom-6 right-6 hidden text-slate-400 transition-colors hover:text-gold md:block"
          aria-label="Scroll to about"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2 }}>
            <ArrowDown size={20} />
          </motion.div>
        </motion.button>
      </motion.div>
    </section>
  );
};

export default Hero;
